import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import en from './en.json'

type TranslationMap = Record<string, string>

/** Translation function. `params` interpolate `{{name}}` placeholders. */
export type TFunc = (key: string, params?: Record<string, unknown>) => string

export interface LanguageOption {
  code: string
  label: string
  short: string
}

export interface LanguageContextValue {
  lang: string
  setLang: (code: string) => void
  t: TFunc
  toggleLanguage: () => void
  languageOptions: LanguageOption[]
}

// en.json carries a handful of nested-object values alongside the flat string
// keys; `t` only ever reads the string-valued keys, so treat the bundle as a
// flat string map for typing purposes.
const enMap = en as unknown as TranslationMap

// English is the fallback locale and ships in the entry bundle. The other
// locales are loaded on demand via dynamic import() — Vite emits each as its
// own chunk, and skipping the eager `<link rel="modulepreload">` for them
// shaves ~400 KB gz off the critical path on first paint (huge LCP/FCP win
// for the landing route where 95% of visitors stay in en/da default).
const translations: Record<string, TranslationMap> = { en: enMap }

// Loader registry. Each entry returns the locale JSON (or null if the user
// requests a locale we don't ship). We keep `en` synchronous so first-paint
// translations resolve without an awaited microtask.
const LOCALE_LOADERS: Record<string, () => Promise<TranslationMap | null>> = {
  en: () => Promise.resolve(enMap),
  da: () => import('./da.json').then(m => m.default as unknown as TranslationMap),
  de: () => import('./de.json').then(m => m.default as unknown as TranslationMap),
  sv: () => import('./sv.json').then(m => m.default as unknown as TranslationMap),
  no: () => import('./no.json').then(m => m.default as unknown as TranslationMap),
}

function loadLocale(code: string): Promise<TranslationMap | null> {
  if (translations[code]) return Promise.resolve(translations[code])
  const loader = LOCALE_LOADERS[code]
  if (!loader) return Promise.resolve(null)
  return loader().then(json => {
    if (json) translations[code] = json
    return json
  })
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

// --- Tenant-type terminology overlay (audit WC-VERT-3 / BILL-10 / PROD-11) ---
// Tenants carry an org_type (theater | venue | gallery | other). Shared-surface
// strings whose theater bias hurts venue/gallery tenants ship sparse overlay
// keys suffixed `__venue` / `__gallery` (double underscore — verified
// collision-free: no base key in any locale contains `__`). Resolution order:
// `key__<org_type>` → `key`, so theater/other tenants — and every key without
// an overlay — resolve exactly as before (zero behavior change by default).
export type OrgType = 'theater' | 'venue' | 'gallery' | 'other'

// Only these org types have overlay vocabularies; theater is the base copy
// and 'other' deliberately falls through to the neutral-ish base too.
const OVERLAY_ORG_TYPES: ReadonlySet<string> = new Set(['venue', 'gallery'])

let _orgType: OrgType = 'theater'
const _orgTypeListeners = new Set<() => void>()

/**
 * Integration point for auth bootstrap: call with the tenant's org_type once
 * /me (or tenantConfig) resolves, and again with 'theater' on logout/switch.
 * Unknown or absent values normalize to 'theater' (base keys). Mounted
 * LanguageProviders re-render their consumers on change.
 */
export function setI18nOrgType(orgType: unknown): void {
  const next: OrgType =
    orgType === 'venue' || orgType === 'gallery' || orgType === 'other'
      ? orgType
      : 'theater'
  if (next === _orgType) return
  _orgType = next
  _orgTypeListeners.forEach(fn => fn())
}

export function getI18nOrgType(): OrgType {
  return _orgType
}

// Resolve a key in `lang` (with en fallback), trying the org_type overlay
// variant first. Overlays are parity-checked across all 5 locales, so if the
// en overlay exists the lang overlay exists too once that locale's chunk
// loads — same fallback semantics as base keys.
function lookup(lang: string, key: string, orgType: OrgType): string | undefined {
  if (OVERLAY_ORG_TYPES.has(orgType)) {
    const overlayKey = `${key}__${orgType}`
    const overlay = translations[lang]?.[overlayKey] ?? translations.en[overlayKey]
    if (overlay !== undefined) return overlay
  }
  return translations[lang]?.[key] ?? translations.en[key]
}

const _warnedKeys = new Set<string>()

const fallbackT: TFunc = (key, params) => {
  const value = lookup('en', key, _orgType)
  if (value === undefined && !_warnedKeys.has(key)) {
    _warnedKeys.add(key)
    console.warn('[i18n] missing translation key:', key, 'lang: en')
  }
  const str = value ?? key
  if (!params) return str
  return str.replace(/\{\{(\w+)\}\}/g, (_, k: string) => String(params[k] ?? `{{${k}}}`))
}

// Module-scope constant: the option list never changes, so it must not be
// rebuilt per render (that would defeat the memoised context value below).
const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'da', label: 'Dansk', short: 'DK' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
  { code: 'sv', label: 'Svenska', short: 'SWE' },
  { code: 'no', label: 'Norsk', short: 'NWE' },
]

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    return {
      lang: 'en',
      setLang: () => {},
      t: fallbackT,
      toggleLanguage: () => {},
      languageOptions: [{ code: 'en', label: 'English', short: 'EN' }],
    }
  }
  return ctx
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<string>(() => {
    return localStorage.getItem('stagewise-lang') || 'en'
  })
  // Bump on async locale load so consumers re-render once translations arrive.
  // Until then, t() transparently falls back to English — no missing-translation
  // flash because en.json is bundled in the entry chunk.
  const [, setReady] = useState(0)
  // Mirror the module-level org_type into state so a setI18nOrgType() call
  // (auth bootstrap) gives `t` a new identity → memoised context value changes
  // → consumers re-render with the overlay vocabulary.
  const [orgType, setOrgType] = useState<OrgType>(_orgType)

  useEffect(() => {
    const sync = () => setOrgType(getI18nOrgType())
    _orgTypeListeners.add(sync)
    sync() // catch a setI18nOrgType() that raced between render and effect
    return () => { _orgTypeListeners.delete(sync) }
  }, [])

  useEffect(() => {
    localStorage.setItem('stagewise-lang', lang)
    if (!translations[lang]) {
      let cancelled = false
      loadLocale(lang).then(json => {
        if (!cancelled && json) setReady(n => n + 1)
      })
      return () => { cancelled = true }
    }
    return undefined
  }, [lang])

  // `t` is referentially stable across a consumer's own re-renders — its
  // identity changes only when `lang` does. This matters: consumers commonly
  // write `const load = useCallback(..., [t]); useEffect(() => load(), [load])`,
  // and a `t` that changed every render would churn that into an infinite
  // update loop. `translations[lang]` is mutated in place as locales load (the
  // setReady bump re-renders consumers so they re-call `t` and read the fresh
  // strings), so a stable identity loses nothing.
  const t = useCallback<TFunc>((key, params) => {
    const value = lookup(lang, key, orgType)
    if (value === undefined && !_warnedKeys.has(key)) {
      _warnedKeys.add(key)
      console.warn('[i18n] missing translation key:', key, 'lang:', lang)
    }
    const str = value ?? key
    if (!params) return str
    return str.replace(/\{\{(\w+)\}\}/g, (_, k: string) => String(params[k] ?? `{{${k}}}`))
  }, [lang, orgType])

  const toggleLanguage = useCallback(() => {
    const idx = LANGUAGE_OPTIONS.findIndex(o => o.code === lang)
    const next = LANGUAGE_OPTIONS[(idx + 1) % LANGUAGE_OPTIONS.length]
    setLang(next.code)
  }, [lang])

  // Memoise the context value so consumers don't re-render (and re-run any
  // effects keyed on the i18n object) on every provider render.
  const value = useMemo<LanguageContextValue>(
    () => ({ lang, setLang, t, toggleLanguage, languageOptions: LANGUAGE_OPTIONS }),
    [lang, t, toggleLanguage]
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}
